import { LinearGradient } from "expo-linear-gradient";
import { useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { Linking, Platform, Pressable, StyleSheet, View } from "react-native";

import {
  DEVICE_LIMIT,
  deviceListLabel,
  deviceSwapResetLabel,
  getDownloadWindow
} from "@recital-master/shared";

import { useAuthState } from "@/auth/AuthProvider";
import { AppHeader } from "@/components/AppHeader";
import {
  DeviceRow,
  deviceDateLabel,
  type DeviceRowData
} from "@/components/DeviceRow";
import { PinkChip } from "@/components/PinkChip";
import { useAccount } from "@/data/AccountProvider";
import { useCatalog, useRecitals } from "@/data/CatalogProvider";
import { deviceSwapBalanceCopy } from "@/devices/deviceSwapCopy";
import { Button } from "@/design/Button";
import { AppDialog } from "@/design/AppDialog";
import {
  CheckIcon,
  ChevronRightIcon,
  DownloadIcon,
  HeartIcon,
  RenewIcon
} from "@/design/icons";
import { Card, Chip, Screen } from "@/design/primitives";
import { Muted, Overline, Text } from "@/design/Text";
import { gradients, hoverRing, palette, radius, space } from "@/design/tokens";
import { useAppRouter } from "@/lib/appRouter";
import {
  createBillingCheckout,
  createBillingPortal,
  firebaseErrorMessage,
  getBillingCheckoutStatus,
  removeDevice
} from "@/firebase/callables";
import { dancersPath } from "@/lib/routes";

const ANNUAL_PRICE = "$65";
const PENDING_CHECKOUT_SESSION_KEY = "recital-master.billing.pending-checkout-session";
const CHECKOUT_CONFIRMATION_TIMEOUT_MS = 60_000;
const CHECKOUT_CONFIRMATION_POLL_MS = 750;
const BILLING_CONFIRMATION_STATES = ["checking", "paid", "processing", "error"] as const;
type BillingConfirmation = (typeof BILLING_CONFIRMATION_STATES)[number];

function pendingCheckoutSession(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(PENDING_CHECKOUT_SESSION_KEY);
}

function rememberCheckoutSession(sessionId: string): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(PENDING_CHECKOUT_SESSION_KEY, sessionId);
}

function forgetCheckoutSession(): void {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(PENDING_CHECKOUT_SESSION_KEY);
  }
}

function waitForCheckoutSync(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, CHECKOUT_CONFIRMATION_POLL_MS));
}

type AccessWindow = {
  until: string;
  devSample: boolean;
  daysLeft: number;
  ended: boolean;
};

/** Noon-anchored so a window never gains or loses a day to the local offset. */
function describeWindow(until: string, devSample: boolean): AccessWindow {
  const now = new Date();
  const noonToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    12
  ).getTime();
  const daysLeft = Math.round(
    (new Date(`${until}T12:00:00`).getTime() - noonToday) / 86_400_000
  );
  return { until, devSample, daysLeft, ended: daysLeft < 0 };
}

/**
 * Access-window display (docs/business-model.md). Until account sync lands,
 * dev mode derives a representative window from the newest recital date.
 *
 * `daysLeft`/`ended` live here so the whole card reads one source of truth.
 * They used not to: the download row compared against now while the headline
 * and status pill did not, so a lapsed account rendered a healthy "Active"
 * pill and "Active until <yesterday>" directly above "your access ended".
 */
function useAccessWindow(
  accessExpiresAt: string | null,
  devMode: boolean
): AccessWindow | null {
  const { catalog } = useCatalog();
  if (accessExpiresAt) {
    return describeWindow(accessExpiresAt.slice(0, 10), devMode);
  }
  if (!devMode) return null;

  const latest = catalog?.performances
    .map((performance) => performance.eventDate)
    .sort()
    .at(-1);
  if (!latest) return null;
  const expires = new Date(`${latest}T12:00:00`);
  expires.setDate(expires.getDate() + 365);
  return describeWindow(expires.toISOString().slice(0, 10), true);
}

/**
 * When the pill starts warming. Sits comfortably before the 30-day download
 * unlock, so a family sees the window closing before downloads appear rather
 * than being surprised by both at once.
 */
const RUNNING_OUT_DAYS = 60;

function daysLeftLabel(daysLeft: number): string {
  if (daysLeft <= 0) return "Ends today";
  if (daysLeft === 1) return "Ends tomorrow";
  return `Ends in ${daysLeft} days`;
}

/** Month-day-year. No weekday — nobody needs to know their access ends on a Thursday. */
function accessDayLabel(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
}

function accessDateLabel(date: Date): string {
  return new Date(`${date.toISOString().slice(0, 10)}T12:00:00`).toLocaleDateString(
    undefined,
    { month: "long", day: "numeric", year: "numeric" }
  );
}

type SampleDevice = DeviceRowData & { thisDevice: boolean; web: boolean };

const sampleDevices: SampleDevice[] = [
  {
    deviceId: "this",
    label: deviceListLabel({
      platform: "web",
      formFactor: "computer",
      operatingSystem: "macOS",
      modelCode: null,
      manufacturer: null,
      browserName: "Chrome"
    }),
    platform: "macOS",
    formFactor: "computer",
    lastUsed: "Jul 13",
    locationLabel: "Winnipeg, MB",
    thisDevice: true,
    web: true
  },
  {
    deviceId: "galaxy",
    label: deviceListLabel({
      platform: "android",
      formFactor: "phone",
      operatingSystem: "Android",
      modelCode: "SM-S901W",
      manufacturer: "samsung",
      browserName: null,
      displayName: "Galaxy S22"
    }),
    platform: "Android",
    formFactor: "phone",
    lastUsed: "Jul 13",
    locationLabel: "Winnipeg, MB",
    thisDevice: false,
    web: false
  },
  {
    deviceId: "phone",
    label: deviceListLabel({
      platform: "ios",
      formFactor: "phone",
      operatingSystem: "iOS",
      modelCode: "iPhone 6s",
      manufacturer: "Apple",
      browserName: null
    }),
    platform: "iOS",
    formFactor: "phone",
    lastUsed: "Jul 12",
    locationLabel: "Winnipeg, MB",
    thisDevice: false,
    web: false
  }
];

export default function AccountScreen() {
  const router = useAppRouter();
  const {
    billing: billingReturn,
    session_id: checkoutSessionId,
    billing_preview: billingPreviewParam
  } = useLocalSearchParams<{
    billing?: string;
    session_id?: string;
    billing_preview?: string;
  }>();
  const billingPreview =
    __DEV__ && BILLING_CONFIRMATION_STATES.includes(billingPreviewParam as BillingConfirmation)
      ? (billingPreviewParam as BillingConfirmation)
      : null;
  const {
    user,
    isAdmin,
    devMode,
    demoMode,
    device: clientDevice,
    deviceLimitReached,
    swapsRemaining,
    nextSwapAvailableAt,
    ensureActiveDevice,
    signOutUser
  } = useAuthState();
  const designPreview = devMode || demoMode;
  const {
    accessExpiresAt,
    billing,
    currentDeviceId,
    devices: activatedDevices,
    deleteAccount,
    downloadsUnlockAtByRecital,
    refreshDevices,
    removeDancer,
    savedDancers
  } = useAccount();
  const recitals = useRecitals();
  const accessWindow = useAccessWindow(accessExpiresAt, designPreview);
  const downloadAccessEndsAt = accessExpiresAt ?? accessWindow?.until ?? null;
  const downloadWindow = downloadAccessEndsAt
    ? getDownloadWindow(downloadAccessEndsAt)
    : null;
  const recitalDownloadWindows = downloadAccessEndsAt
    ? recitals.map((recital) => ({
        recitalId: recital.recitalId,
        window: getDownloadWindow(
          downloadAccessEndsAt,
          new Date(),
          downloadsUnlockAtByRecital[recital.recitalId]
        )
      }))
    : [];
  const unlockedRecitalDownloads = recitalDownloadWindows.filter(
    ({ window }) => window.status === "unlocked"
  ).length;
  const lockedRecitalDownloads = recitalDownloadWindows.filter(
    ({ window }) => window.status === "locked"
  );
  const nextRecitalUnlockDate = lockedRecitalDownloads
    .map(({ window }) => window.unlockDate)
    .sort((a, b) => a.getTime() - b.getTime())[0] ?? null;
  const downloadAvailabilityText =
    downloadWindow?.status === "expired"
      ? `Dance downloads were available until your access ended on ${accessDateLabel(downloadWindow.accessEndDate)}.`
      : unlockedRecitalDownloads > 0 && lockedRecitalDownloads.length > 0
        ? "Dance downloads are available now for selected recitals. Other recital downloads unlock later."
        : unlockedRecitalDownloads > 0 || downloadWindow?.status === "unlocked"
          ? `Dance downloads are available until your access ends on ${accessDateLabel(downloadWindow!.accessEndDate)}.`
          : nextRecitalUnlockDate
            ? `Dance downloads unlock on ${accessDateLabel(nextRecitalUnlockDate)}.`
            : downloadWindow?.status === "locked"
              ? `Dance downloads unlock on ${accessDateLabel(downloadWindow.unlockDate)}.`
              : null;
  const devices: SampleDevice[] = designPreview
    ? sampleDevices
    : activatedDevices.map((device) => ({
        deviceId: device.deviceId,
        label: deviceListLabel(device),
        platform: device.operatingSystem,
        formFactor: device.formFactor,
        lastUsed: deviceDateLabel(device.lastUsedAt),
        locationLabel: device.locationLabel ?? null,
        thisDevice: device.deviceId === currentDeviceId,
        web: device.platform === "web"
      }));
  const [pendingDeviceIds, setPendingDeviceIds] = useState<Record<string, true>>({});
  const [removeErrors, setRemoveErrors] = useState<Record<string, string>>({});
  const [deviceToRemove, setDeviceToRemove] = useState<SampleDevice | null>(null);
  const [billingPending, setBillingPending] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [checkoutConfirmation, setCheckoutConfirmation] =
    useState<BillingConfirmation | null>(
      billingPreview ?? (billingReturn === "success" ? "checking" : null)
    );
  const [confirmedAccessExpiresAt, setConfirmedAccessExpiresAt] = useState<
    string | null
  >(billingPreview === "paid" ? "2028-07-07T12:00:00.000Z" : null);
  const newestRecital = [...recitals].sort((a, b) => b.year - a.year)[0];
  const currentDeviceIsActive = devices.some((device) => device.thisDevice);
  const hasManagedSubscription =
    billing?.subscriptionId != null &&
    billing.status != null &&
    ["active", "trialing", "past_due", "unpaid"].includes(billing.status);
  const webhookConfirmsRenewal =
    hasManagedSubscription && billing?.lastPaidInvoiceId != null;
  const paymentIssue = billing?.status === "past_due" || billing?.status === "unpaid";
  const renewalIsOff = !paymentIssue && billing?.cancelAtPeriodEnd === true;
  const renewalEndDate = billing?.currentPeriodEndsAt ?? accessWindow?.until ?? null;
  // Store rules are decided by the platform, never by our own preview flags.
  // `designPreview` used to short-circuit all three of these, so demo mode —
  // the exact path App Review is instructed to walk — rendered a dead
  // "Extend for another year · $65" button on iOS and hid the compliant
  // native note. See the constraint table in docs/business-model.md.
  const isWeb = Platform.OS === "web";
  // Demo has no account and nothing to renew; dev keeps it so layout is judgeable.
  const showBillingAction = isWeb && (devMode || !demoMode);
  const showRenewalDetails = Platform.OS !== "ios";
  const showNativeAccountNote = !isWeb;

  // The pill carries time, not status. Colour carries health, so no state
  // ever needs a defeatist word: accent → warning → neutral as the window
  // runs down. Families who bought at the recital have no `billing` object at
  // all, so "Renews yearly" is reserved for accounts that genuinely renew.
  const accessStatus = !accessWindow
    ? null
    : paymentIssue
      ? { label: "Payment issue", tone: "danger" as const }
      : accessWindow.ended
        ? { label: "Ended", tone: "neutral" as const }
        : renewalIsOff
          ? { label: daysLeftLabel(accessWindow.daysLeft), tone: "warning" as const }
          : hasManagedSubscription
            ? { label: "Renews yearly", tone: "accent" as const }
            : accessWindow.daysLeft <= RUNNING_OUT_DAYS
              ? { label: daysLeftLabel(accessWindow.daysLeft), tone: "warning" as const }
              : {
                  label: `${accessWindow.daysLeft} days left`,
                  tone: "accent" as const
                };
  const ghostDevice: DeviceRowData = {
    deviceId: clientDevice?.deviceId ?? "current-device",
    label: clientDevice ? deviceListLabel(clientDevice) : "This device",
    platform: clientDevice?.operatingSystem ?? "",
    formFactor: clientDevice?.formFactor ?? "phone"
  };

  useEffect(() => {
    if (billingPreview) {
      setCheckoutConfirmation(billingPreview);
      setConfirmedAccessExpiresAt(
        billingPreview === "paid" ? "2028-07-07T12:00:00.000Z" : null
      );
      return;
    }
    if (billingReturn === "cancelled") {
      forgetCheckoutSession();
      router.replace("/account");
      return;
    }
    if (billingReturn !== "success") return;
    const sessionId = checkoutSessionId || pendingCheckoutSession();
    if (!sessionId) {
      setCheckoutConfirmation(webhookConfirmsRenewal ? "paid" : "processing");
      return;
    }

    let active = true;
    setCheckoutConfirmation("checking");
    void (async () => {
      const deadline = Date.now() + CHECKOUT_CONFIRMATION_TIMEOUT_MS;
      let reachedStripe = false;
      while (active && Date.now() < deadline) {
        try {
          const result = await getBillingCheckoutStatus(sessionId);
          if (!active) return;
          reachedStripe = true;
          if (result.renewalConfirmed) {
            forgetCheckoutSession();
            setConfirmedAccessExpiresAt(result.accessExpiresAt);
            setCheckoutConfirmation("paid");
            return;
          }
        } catch (error) {
          if (__DEV__) console.warn("Billing confirmation retrying", error);
        }
        await waitForCheckoutSync();
      }
      if (!active) return;
      if (webhookConfirmsRenewal) forgetCheckoutSession();
      setCheckoutConfirmation(
        webhookConfirmsRenewal ? "paid" : reachedStripe ? "processing" : "error"
      );
    })();
    return () => {
      active = false;
    };
  }, [billingPreview, billingReturn, checkoutSessionId, router, webhookConfirmsRenewal]);

  function dismissCheckoutConfirmation() {
    forgetCheckoutSession();
    setCheckoutConfirmation(null);
    router.replace("/account");
  }

  function leaveCheckoutConfirmation() {
    forgetCheckoutSession();
    setCheckoutConfirmation(null);
    router.replace(checkoutConfirmation === "paid" ? "/recitals" : "/account");
  }

  async function confirmDeviceRemoval() {
    const device = deviceToRemove;
    if (!device || pendingDeviceIds[device.deviceId]) return;
    setPendingDeviceIds((current) => ({ ...current, [device.deviceId]: true }));
    setRemoveErrors((current) => {
      const next = { ...current };
      delete next[device.deviceId];
      return next;
    });
    try {
      await removeDevice(device.deviceId);
      if (device.thisDevice) {
        await signOutUser();
        router.replace("/login");
        return;
      }
      await ensureActiveDevice();
      await refreshDevices();
    } catch {
      setPendingDeviceIds((current) => {
        const next = { ...current };
        delete next[device.deviceId];
        return next;
      });
      setRemoveErrors((current) => ({
        ...current,
        [device.deviceId]: "Couldn't remove this device — try again."
      }));
    } finally {
      setDeviceToRemove(null);
    }
  }

  const swapResetLabel = deviceSwapResetLabel(nextSwapAvailableAt);
  const replacementDelayCopy =
    swapsRemaining === 0
      ? swapResetLabel
        ? ` You can add a replacement on ${swapResetLabel}.`
        : " You can add a replacement next month."
      : "";

  const confirmationCopy =
    checkoutConfirmation === "paid"
      ? confirmedAccessExpiresAt || accessWindow
        ? `Your access now runs through ${accessDayLabel(confirmedAccessExpiresAt?.slice(0, 10) ?? accessWindow!.until)}. Your front-row seat is ready when you are.`
        : "Your renewal is confirmed. Your updated access date will appear here in just a moment."
      : checkoutConfirmation === "processing"
        ? "Your payment is processing. Your access will update here automatically."
        : checkoutConfirmation === "error"
          ? "Stripe hasn't confirmed it yet — this usually lands within a minute. Your access below will update on its own."
          : "This usually takes just a moment.";

  return (
    <>
      <AppDialog
        visible={deleteConfirmationVisible}
        title="Delete your account?"
        body="This permanently deletes your account — recital access, saved dancers, viewing progress, and registered devices — and signs you out everywhere. Any active renewal is cancelled. This can't be undone."
        onRequestClose={() => {
          if (!deletePending) setDeleteConfirmationVisible(false);
        }}
        actions={
          <>
            <Button
              label="Cancel"
              variant="ghost"
              disabled={deletePending}
              onPress={() => setDeleteConfirmationVisible(false)}
            />
            <Button
              label="Delete account"
              variant="secondary"
              loading={deletePending}
              onPress={() => {
                if (deletePending) return;
                setDeletePending(true);
                setDeleteError(null);
                void deleteAccount()
                  .then(() => router.replace("/login"))
                  .catch((error: unknown) => {
                    setDeleteError(
                      error instanceof Error ? error.message : "Account deletion failed."
                    );
                    setDeletePending(false);
                  });
              }}
            />
          </>
        }
      >
        {deleteError ? (
          <Text variant="small" color={palette.danger}>
            {deleteError}
          </Text>
        ) : null}
      </AppDialog>
      <AppDialog
        visible={checkoutConfirmation != null}
        title={
          checkoutConfirmation === "paid"
            ? "You’re set for another year."
            : checkoutConfirmation === "processing"
              ? "We’re finalizing your renewal."
              : checkoutConfirmation === "error"
                ? "Still confirming your renewal."
                : "Finishing your renewal…"
        }
        body={confirmationCopy}
        leading={
          checkoutConfirmation === "paid" ? (
            <LinearGradient
              colors={gradients.brand as unknown as [string, string, ...string[]]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.confirmationIcon}
            >
              <CheckIcon size={22} color={palette.inkOnAccent} />
            </LinearGradient>
          ) : null
        }
        onRequestClose={dismissCheckoutConfirmation}
        actions={
          checkoutConfirmation === "checking" ? (
            <Button label="Finalizing" loading disabled />
          ) : (
            <Button
              label={checkoutConfirmation === "paid" ? "Enjoy the show" : "Got it"}
              onPress={leaveCheckoutConfirmation}
            />
          )
        }
      />
      <AppDialog
        visible={deviceToRemove != null}
        title={
          deviceToRemove?.thisDevice
            ? "Remove this device?"
            : `Remove ${deviceToRemove?.label ?? "device"}?`
        }
        body={
          deviceToRemove
            ? `${
                deviceToRemove.thisDevice
                  ? "You'll be signed out on this device immediately."
                  : deviceToRemove.web
                    ? "This browser loses access immediately."
                    : "It loses access immediately."
              }${
                deviceToRemove.web ? " No swap needed." : replacementDelayCopy
              }`
            : undefined
        }
        onRequestClose={() => {
          if (!deviceToRemove || !pendingDeviceIds[deviceToRemove.deviceId]) {
            setDeviceToRemove(null);
          }
        }}
        actions={
          <>
            <Button
              label="Cancel"
              variant="ghost"
              disabled={
                deviceToRemove != null && pendingDeviceIds[deviceToRemove.deviceId] === true
              }
              onPress={() => setDeviceToRemove(null)}
            />
            <Button
              label="Remove"
              loading={
                deviceToRemove != null && pendingDeviceIds[deviceToRemove.deviceId] === true
              }
              onPress={() => void confirmDeviceRemoval()}
            />
          </>
        }
      />
      <Screen enter="rise" header={<AppHeader back />}>
      <View style={styles.header}>
        <Overline>Your family account</Overline>
        <Text variant="display">Account</Text>
        <Muted>{designPreview ? "family@example.com" : user?.email ?? ""}</Muted>
      </View>

      <View style={styles.cards}>
        {/* Access window — the thing families actually need to know */}
        <Card>
          <View style={styles.cardHeader}>
            <Text variant="title">Your library</Text>
            {accessStatus ? (
              <Chip tone={accessStatus.tone} dot>
                {accessStatus.label}
              </Chip>
            ) : null}
          </View>
          {accessWindow ? (
            <>
              {/* The fact, not the label, is the loudest thing on the card.
                  "Through" over "until": it settles whether the last day
                  counts. No possessive here — the card title already says
                  "Your access", and repeating it read as twee. */}
              <Text variant="bodyStrong" color={palette.text} style={styles.accessLine}>
                {accessWindow.ended
                  ? `Ended ${accessDayLabel(accessWindow.until)}`
                  : `Active through ${accessDayLabel(accessWindow.until)}`}
              </Text>
              {showRenewalDetails ? (
                <View style={styles.accessRow}>
                  <RenewIcon size={15} color={paymentIssue ? palette.danger : palette.accent} />
                  <Muted variant="small" style={styles.accessRowText}>
                    {paymentIssue ? (
                      "Update your payment method to keep your access."
                    ) : accessWindow.ended ? (
                      "Every recital is still here. Extending brings them right back."
                    ) : renewalIsOff && renewalEndDate ? (
                      `Renewal is off — access ends ${accessDayLabel(renewalEndDate.slice(0, 10))}.`
                    ) : hasManagedSubscription ? (
                      // Price only on web: Canada sits in no Apple or Google
                      // external-purchase programme, and whether a numeric
                      // off-app price is permitted in consumption-only native
                      // copy is unverified. Not a gamble worth taking on a
                      // first submission. See docs/business-model.md.
                      isWeb ? (
                        <>
                          Renews at{" "}
                          <Text variant="smallStrong" color={palette.textSoft}>
                            {ANNUAL_PRICE}/year
                          </Text>{" "}
                          — keeps every recital you own.
                        </>
                      ) : (
                        "Renews yearly — keeps every recital you own."
                      )
                    ) : (
                      "Extending keeps every recital you own."
                    )}
                  </Muted>
                </View>
              ) : null}
              <View style={styles.accessRow}>
                <DownloadIcon size={15} color={palette.accent} />
                <Muted variant="small" style={styles.accessRowText}>
                  {downloadAvailabilityText}
                </Muted>
              </View>
              {showBillingAction ? (
                <View style={styles.renewPreview}>
                  <View style={styles.footerButton}>
                    <Button
                      label={
                        paymentIssue
                          ? "Fix payment"
                          : renewalIsOff
                            ? "Turn renewal back on"
                            : hasManagedSubscription
                              ? "Billing settings"
                              : `Extend for another year · ${ANNUAL_PRICE}`
                      }
                      variant={
                        paymentIssue || renewalIsOff || hasManagedSubscription
                          ? "secondary"
                          : "tinted"
                      }
                      loading={billingPending}
                      onPress={async () => {
                        if (designPreview || billingPending) return;
                        setBillingPending(true);
                        setBillingError(null);
                        try {
                          let url: string;
                          if (hasManagedSubscription) {
                            ({ url } = await createBillingPortal());
                          } else {
                            const checkout = await createBillingCheckout();
                            url = checkout.url;
                            rememberCheckoutSession(checkout.sessionId);
                          }
                          await Linking.openURL(url);
                          setBillingPending(false);
                        } catch (error) {
                          setBillingError(firebaseErrorMessage(error));
                          setBillingPending(false);
                        }
                      }}
                    />
                  </View>
                  {billingError ? (
                    <Text variant="small" color={palette.danger} style={styles.billingError}>
                      {billingError}
                    </Text>
                  ) : null}
                </View>
              ) : null}
              {showNativeAccountNote ? (
                <Muted variant="small" style={styles.nativeAccountNote}>
                  {/* Passive, never imperative, and identical in both
                      branches. "Manage access at …" is a call to action, which
                      is exactly what anti-steering prohibits outside the US
                      storefront. Stating where access lives is a fact; telling
                      someone to go there is steering. */}
                  Access is managed at RecitalMaster.com
                </Muted>
              ) : null}
            </>
          ) : (
            <>
              <Muted>No active access on this account yet.</Muted>
              {showNativeAccountNote ? (
                <Muted variant="small" style={styles.nativeAccountNote}>
                  Access is managed at RecitalMaster.com
                </Muted>
              ) : null}
            </>
          )}
        </Card>

        {/* My dancers */}
        <Card>
          <View style={styles.cardHeader}>
            <Text variant="title">My dancers</Text>
            <Chip tone="pink" icon={<HeartIcon size={11} color={palette.pink} />}>
              {savedDancers.length}
            </Chip>
          </View>
          <Muted variant="small" style={styles.cardCopy}>
            {savedDancers.length === 0
              ? "Unlock one-tap playlists for all your dancers."
              : "Your dancers are ready to play in one tap."}
          </Muted>
          {savedDancers.length === 0 ? (
            <View style={styles.footerButton}>
              {newestRecital ? (
                <PinkChip
                  label="Find my dancer"
                  fullWidth
                  onPress={() => router.push(dancersPath(newestRecital) as never)}
                />
              ) : null}
            </View>
          ) : (
            <>
              {savedDancers.map((dancer) => (
                <View key={dancer.dancerId} style={styles.dancerLine}>
                  <View style={styles.dancerName}>
                    <HeartIcon size={14} color={palette.pink} />
                    <Text variant="bodyStrong" color={palette.pink}>
                      {dancer.name}
                    </Text>
                  </View>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`Remove ${dancer.name}`}
                    onPress={() => removeDancer(dancer.dancerId)}
                    style={({ hovered, focused }) => [
                      styles.removeChip,
                      (hovered || focused) && styles.removeChipHovered
                    ]}
                  >
                    <Text variant="smallStrong" color={palette.muted}>
                      Remove
                    </Text>
                  </Pressable>
                </View>
              ))}
              {newestRecital ? (
                <View style={styles.addDancerRow}>
                  <PinkChip
                    label="Add a dancer"
                    fullWidth
                    onPress={() => router.push(dancersPath(newestRecital) as never)}
                  />
                </View>
              ) : null}
            </>
          )}
        </Card>

        {/* Devices */}
        <Card>
          <View style={styles.cardHeader}>
            <Text variant="title">Devices</Text>
            <Chip>{devices.length} of {DEVICE_LIMIT}</Chip>
          </View>
          <Muted variant="small" style={styles.cardCopy}>
            Your family account can watch on up to {DEVICE_LIMIT} devices.
          </Muted>
          {deviceLimitReached && !currentDeviceIsActive ? (
            <DeviceRow
              device={ghostDevice}
              ghost
              secondaryLine="Not registered — remove a device to add it"
              style={styles.ghostDeviceRow}
            />
          ) : null}
          {devices.map((device) => (
            <DeviceRow
              key={device.deviceId}
              device={device}
              actionLabel={designPreview ? undefined : "Remove"}
              pending={pendingDeviceIds[device.deviceId] === true}
              error={removeErrors[device.deviceId] ?? null}
              style={styles.deviceRowSpacing}
              onAction={() => {
                if (!pendingDeviceIds[device.deviceId]) setDeviceToRemove(device);
              }}
            />
          ))}
          <Muted variant="small" style={styles.deviceQuotaNote}>
            {deviceSwapBalanceCopy(swapsRemaining, nextSwapAvailableAt)}
          </Muted>
          {designPreview ? (
            <Muted variant="small" style={styles.devNote}>
              Sample data — device management activates with sign-in.
            </Muted>
          ) : null}
        </Card>

        {/* Admin — visible only to the studio owner (and local design preview;
            a store build's demo mode never shows this — __DEV__ is false there) */}
        {isAdmin || devMode || (__DEV__ && demoMode) ? (
          <Pressable
            accessibilityRole="button"
            onPress={() => router.push("/admin" as never)}
            style={({ hovered, focused }) => [
              styles.adminCard,
              (hovered || focused) && styles.adminCardHovered
            ]}
          >
            <View style={styles.adminCardText}>
              <Text variant="title">Admin dashboard</Text>
              <Muted variant="small">
                Watch stats, top dances, and family accounts — only you can see this.
              </Muted>
            </View>
            <ChevronRightIcon size={18} color={palette.muted} />
          </Pressable>
        ) : null}

        {!devMode || demoMode ? (
          <Button
            label={demoMode ? "Exit demo" : "Sign out"}
            variant="secondary"
            onPress={async () => {
              await signOutUser();
              router.replace("/login");
            }}
          />
        ) : null}
        {demoMode ? (
          <Muted variant="small" style={styles.devFootnote}>
            Demo mode uses fictional dancers and public synthetic recital media.
          </Muted>
        ) : devMode ? (
          <Muted variant="small" style={styles.devFootnote}>
            Design preview — showing the signed-in experience with sample data.
          </Muted>
        ) : null}
        {!designPreview ? (
          <Button
            label="Delete account"
            variant="secondary"
            onPress={() => {
              setDeleteError(null);
              setDeleteConfirmationVisible(true);
            }}
          />
        ) : null}
      </View>
      </Screen>
    </>
  );
}

const styles = StyleSheet.create({
  header: {
    gap: space.sm,
    marginBottom: space.xl,
    marginTop: 0
  },
  cards: {
    gap: space.lg
  },
  adminCard: {
    alignItems: "center",
    backgroundColor: palette.surface,
    borderColor: palette.hairline,
    borderRadius: radius.lg,
    borderWidth: 1,
    flexDirection: "row",
    gap: space.lg,
    justifyContent: "space-between",
    padding: space.xl
  },
  adminCardHovered: {
    borderColor: hoverRing.borderColor
  },
  adminCardText: {
    flex: 1,
    gap: 2
  },
  cardHeader: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between"
  },
  // Steps up from body so the date outweighs the "Your access" label above it.
  accessLine: {
    fontSize: 17,
    lineHeight: 24,
    marginTop: space.md
  },
  accessRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.sm,
    marginTop: space.sm
  },
  accessRowText: {
    flex: 1
  },
  devFootnote: {
    textAlign: "center"
  },
  // No bottom margin: whatever follows (rows at space.md, actions at
  // space.lg via footerButton) owns the gap, so it can't double-stack.
  cardCopy: {
    marginTop: space.sm
  },
  addDancerRow: {
    marginTop: space.lg
  },
  footerButton: {
    marginTop: space.lg
  },
  dancerLine: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: space.md
  },
  dancerName: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.sm
  },
  deviceRowSpacing: {
    marginTop: space.md
  },
  ghostDeviceRow: {
    marginTop: space.md
  },
  deviceQuotaNote: {
    marginTop: space.lg,
    textAlign: "center"
  },
  // Post-divider rhythm: body → action is exactly one space.lg everywhere
  // (footerButton carries it); wrappers must not stack a second margin.
  renewPreview: {},
  // Left, not centred: this is body copy, not a caption under a button. On
  // native there is no CTA beneath it, so centring left it a stray axis.
  nativeAccountNote: {
    marginTop: space.lg
  },
  billingError: {
    marginTop: space.sm,
    textAlign: "center"
  },
  confirmationIcon: {
    alignItems: "center",
    alignSelf: "flex-start",
    backgroundColor: palette.accent,
    borderRadius: radius.pill,
    height: 44,
    justifyContent: "center",
    marginBottom: space.sm,
    width: 44
  },
  removeChip: {
    alignItems: "center",
    borderColor: palette.hairlineStrong,
    borderRadius: radius.pill,
    borderWidth: 1,
    justifyContent: "center",
    minWidth: 76,
    paddingHorizontal: space.md,
    paddingVertical: 4
  },
  removeChipHovered: {
    backgroundColor: palette.coolWash
  },
  devNote: {
    marginTop: space.md
  }
});
